import * as React from "react";

import {
  AuthenticatedTemplate,
  MsalProvider,
  UnauthenticatedTemplate,
  useIsAuthenticated,
} from "@azure/msal-react";
import { makeStyles } from "@fluentui/react-components";
import { HashRouter, Route, Routes, useLocation } from "react-router-dom";

import SignIn from "./pages/SignIn/SignIn";
import { privateRoutes } from "./routes";
import Footer from "../../components/Footer/Footer";
import Header from "../../components/Header/Header";
import Sidebar from "../../components/Sidebar/Sidebar";
import { msalService } from "../../service/msal.service";

interface AppProps {
  title: string;
}

// Routes that should show the "Back to Analysis" button
const routesWithBackButton = ["/resolving", "/view-cases", "/certification-report"];

const FooterWithRoute: React.FC<{ className?: string }> = ({ className }) => {
  const location = useLocation();
  const showBackButton = routesWithBackButton.includes(location.pathname);
  return <Footer className={className} showBackButton={showBackButton} />;
};

const useStyles = makeStyles({
  root: {
    height: "100vh",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    position: "sticky",
    top: 0,
    zIndex: 100,
  },
  main: {
    flex: 1,
    overflow: "auto",
    padding: "20px",
    minHeight: 0, // Important for flex overflow to work
  },
  footer: {
    position: "sticky",
    bottom: 0,
    zIndex: 100,
  },
});

// Component to handle navigation from external commands (like ribbon Clear button)
const NavigationHandler: React.FC = () => {
  React.useEffect(() => {
    // Check for navigation flag from ribbon command
    const checkNavigationFlag = () => {
      // First check Office Settings (cross-context communication)
      try {
        const officeFlag = Office.context?.document?.settings?.get("benchly_navigate_cleaning");
        if (officeFlag) {
          Office.context.document.settings.remove("benchly_navigate_cleaning");
          Office.context.document.settings.saveAsync();
          window.location.hash = "#/cleaning";
          return;
        }
      } catch (error) {
        console.log("Could not check Office settings:", error);
      }

      // Fallback to localStorage (for backwards compatibility)
      const flag = localStorage.getItem("benchly_navigate_cleaning");
      if (flag) {
        localStorage.removeItem("benchly_navigate_cleaning");
        window.location.hash = "#/cleaning";
      }
    };

    // Check on mount
    checkNavigationFlag();

    // Poll for Office settings changes (since settings don't have event listeners)
    const interval = setInterval(checkNavigationFlag, 500);

    // Also listen for storage events (from other contexts like commands.ts)
    const handleStorage = (e: StorageEvent) => {
      if (e.key === "benchly_navigate_cleaning" && e.newValue) {
        checkNavigationFlag();
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  return null;
};

const AppContent: React.FC<AppProps> = (props: AppProps) => {
  const styles = useStyles();
  const isAuthenticated = useIsAuthenticated();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  React.useEffect(() => {
    if (!isAuthenticated) {
      setSidebarOpen(false);
    }
  }, [isAuthenticated]);

  return (
    <div className={styles.root}>
      <NavigationHandler />

      <Header
        className={styles.header}
        logo="assets/beyond-logo.svg"
        title={props.title}
        onMenuClick={isAuthenticated ? () => setSidebarOpen(true) : undefined}
      />
      <AuthenticatedTemplate>
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <main className={styles.main}>
          <SignIn />
        </main>
      </UnauthenticatedTemplate>

      <AuthenticatedTemplate>
        <main className={styles.main}>
          <Routes>
            {privateRoutes.map(({ path, element }) => (
              <Route key={path} path={path} element={element} />
            ))}
          </Routes>
        </main>
        <FooterWithRoute className={styles.footer} />
      </AuthenticatedTemplate>
    </div>
  );
};

const App: React.FC<AppProps> = (props: AppProps) => {
  return (
    <MsalProvider instance={msalService.instance}>
      <HashRouter>
        <AppContent {...props} />
      </HashRouter>
    </MsalProvider>
  );
};

export default App;
